import Vue from 'vue';
// Common error codes
import * as eCodes from '@/utilities/errorCodes';
// Common phrases for error response
import { SOMETHING_WENT_WRONG, REFRESH, HAPPENS_AGAIN } from '@/utilities/errorResponses';
import formatGraphQlError from '@/utilities/formatGraphQlError';

// Errors that we look for generally in graphql requests
const generalErrors = [eCodes.FORBIDDEN, eCodes.STORE_GET_ERROR, eCodes.INTERNAL_SERVER_ERROR];

// Our AppState definitions
const appStateData = {
	cartIsOpen: false,
	cart: [],
	productClasses: [],
	productColors: [],
	productMaterials: [],
	productSizes: [],
	showFilters: false,
	appModalMsg: '',
	appModalTitle: '',
	appModalFn: null
};

// The global app state
class AppState {
	constructor() {
		this.data = { ...appStateData };

		// Get the cart from localStorage if there is anything stored in localStorage
		const storageCart = localStorage.getItem('cart');
		if (storageCart) {
			this.data.cart = JSON.parse(storageCart);
		}

		// Bind functions
		this.set = this.set.bind(this);
		this.addToCart = this.addToCart.bind(this);
		this.updateCart = this.updateCart.bind(this);
		this.showModalMsg = this.showModalMsg.bind(this);
		this.showGeneralError = this.showGeneralError.bind(this);
		this.clearAll = this.clearAll.bind(this);
		this.clearCart = this.clearCart.bind(this);
	}

	// Method that can update the value of any AppState property
	set(property, value) {
		Vue.set(this.data, property, value);
	}

	// Adds the desired sku and quantity to cart
	addToCart(sku, quantity) {
		const matchingItem = this.data.cart.filter(item => item.sku === sku);

		// Check to see if sku already in cart
		if (matchingItem.length > 0) {
			// Update the quantity
			matchingItem.map(item => (item.quantity += quantity));
		} else {
			// If not in cart, add it to the cart
			this.data.cart.push({ sku, quantity });
		}

		// Update the local storage
		localStorage.setItem('cart', JSON.stringify(this.data.cart));
		Vue.set(this.data, 'cart', this.data.cart);
	}

	// Updates the sku in the cart to the desired quantity, if the quantity is 0 then it removes it from the cart
	updateCart(product, quantity) {
		const cart = this.data.cart;
		const itemIndex = cart.findIndex(item => item.sku === product.sku);

		// If the desired quantity is 0, remove from cart
		if (quantity === 0) {
			cart.splice(itemIndex, 1);

			if (product !== undefined) {
				// Send product information to Google Analytics
				Vue.$ga.ecommerce.addProduct({
					id: product.sku,
					name: product.class,
					variant: product.color + ' - ' + product.size,
					category: product.material
				});

				// Marking this specific action as a 'remove' action (Remove from Cart)
				Vue.$ga.ecommerce.setAction('remove');

				// This event is required to send the two previous actions to Google Analytics. Also used to tag
				// this event as an Ecommerce: Remove from Cart event
				Vue.$ga.event({
					eventCategory: 'Enhanced Ecommerce',
					eventAction: 'Click',
					eventLabel: 'Remove from Cart'
				});
			}
		} else {
			// Otherwise, update the cart sku to the desired quantity
			cart.filter(item => item.sku === product.sku).map(item => (item.quantity = quantity));
		}

		// Update the local storage
		localStorage.setItem('cart', JSON.stringify(this.data.cart));
		Vue.set(this.data, 'cart', this.data.cart);
	}

	/**
	 * A convenience method that enables anything in the application to display a site-wide modal. Having a `msg` auto-opens the modal. Therefore, if you want to close it, then empty the `msg`.
	 *
	 * @param {String} msg - the body of the message you would like to show
	 * @param {String} title - the title you would like on the modal
	 * @param {Function} preCloseAction - a function you would like to be executed immediately before the modal closes
	 */
	showModalMsg(msg, title = '', preCloseAction = null) {
		Vue.set(this.data, 'appModalMsg', msg);
		Vue.set(this.data, 'appModalTitle', title);
		Vue.set(this.data, 'appModalFn', preCloseAction);
	}

	/**
	 * A convenient interception method that checks whether or not you got a common/generic error response from Apollo, and shows
	 *
	 * @param {ApolloResponse} error - if `networkError` or `graphQLErrors` is not null, you've got yourself an error
	 * @returns {ApolloResponse} - IF there is no general error, then it will pass back a formatted error object
	 */
	showGeneralError(error) {
		// All return values must be an object, either with an `error` property or none at all
		const response = {};

		if (error.networkError) {
			// Oh no! A network error! Show the modal!
			this.showModalMsg("We're detecting network issues. Please check your connection and try again.", 'Something went wrong.');
		} else if (error.graphQLErrors && error.graphQLErrors[0] && generalErrors.indexOf(error.graphQLErrors[0].extensions.code) > -1) {
			// Got a general error case - let's show an appropriate error message
			switch (error.graphQLErrors[0].extensions.code) {
				case eCodes.FORBIDDEN:
					// Dirty, rotten, little....
					this.showModalMsg('You are not authorized to perform that function.', 'Forbidden');
					break;
				case eCodes.STORE_GET_ERROR:
					this.showModalMsg(
						`Sorry, we seem to be experiencing difficulties fetching the data for your store. ${REFRESH} ${HAPPENS_AGAIN}`,
						SOMETHING_WENT_WRONG
					);
					break;
				case eCodes.INTERNAL_SERVER_ERROR:
					this.showModalMsg(
						`Sorry, we seem to be experiencing difficulties with our servers right now. ${HAPPENS_AGAIN}`,
						SOMETHING_WENT_WRONG
					);
					break;
			}
		} else {
			// Only return the error if the calling code can use it
			response.error = formatGraphQlError(error);
		}

		// Return no matter what
		return response;
	}

	// Wipes everything out that we have available directly through our props
	clearAll() {
		Vue.set(this, 'data', { ...appStateData });

		// Clear cart localStorage
		localStorage.removeItem('cart');
	}

	// Clears out the cart in all locations
	clearCart() {
		localStorage.setItem('cart', JSON.stringify([]));
		Vue.set(this.data, 'cart', []);
	}
}

export default Vue.observable(new AppState());
